import type { FC } from 'react'

import { Icon, Select, Textarea } from '@skytech/ui'
import { toFirstUpperCase } from '@skytech/utils'
import { uniq } from '@skytech/utils/list-operations'
import { useQuery } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field } from 'formik'
import OpenInNewIcon from 'mdi-react/OpenInNewIcon'
import { useParams } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import RouterLink from '@/components/RouterLink'
import {
  ADMIN_ROLES,
  USER_ROLE,
  useHasAssetEditingAccess,
  useHasRoleBasedAccess,
} from '@/hooks/access'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'
import type { AssetExtended } from '@/modules/admin/resources'
import { ONLY_LEASED_ASSET_TYPES } from '@/modules/admin/resources/assets'
import { leasingResources } from '@/resources/leasing'
import { APP_ROUTES } from '@/util/app-routes'

import { getCostObjectById } from '../helpers'

import { NotificationLogPopup } from './NotificationLogPopup'

export const UserInfo: FC<{
  assetDetails: AssetExtended
  onChangeUserTypeId: (userTypeId: number) => void
  isFormDisabled?: boolean
}> = ({ assetDetails, onChangeUserTypeId, isFormDisabled = false }) => {
  const translate = useTranslation()
  const hasEditAccess = useHasAssetEditingAccess()

  const params = useParams()
  const assetId = Number(params.id)

  if (isNaN(assetId)) throw Error(translate('Asset ID is not a number!'))

  const hasLocationsAccess = useHasRoleBasedAccess([
    ...ADMIN_ROLES,
    USER_ROLE.IT_USER,
    USER_ROLE.IT_PURCHASER,
  ])
  const { data: assetTypes } = useQuery(queries.adminAssetTypes(), adminResources.getAssetTypes)
  const { data: customers } = useQuery(queries.adminCustomers(), adminResources.getCustomers)
  const { data: addresses } = useQuery(queries.adminAddresses(), adminResources.getAddressList)
  const { data: employees } = useQuery(queries.adminEmployeeList(hasLocationsAccess), () =>
    adminResources.getEmployees(hasLocationsAccess)
  )

  const { data: leasingInfo } = useQuery(
    queries.adminLeasingAssetDetails(assetId),
    () => leasingResources.getLeasingAssetDetails(assetId),
    { enabled: Boolean(assetDetails) && assetDetails.isLeased }
  )

  const addressOptions =
    addresses?.map(({ id, zip, city, address }) => ({
      value: id,
      label: `${address}, ${zip} ${city}`,
    })) || []

  const assetStatusOptions =
    assetTypes?.statuses
      ?.filter(assetStatus => {
        if (!assetDetails.isLeased) return !ONLY_LEASED_ASSET_TYPES.includes(assetStatus.status)
        return true
      })
      .map(({ id, status }) => ({
        value: id,
        label: toFirstUpperCase(translate(`asset_status/${status}`)),
      })) || []

  if (assetDetails.customerAddressId) {
    // Current address might be absent in the list (e.g. when asset migrates from another customer)
    addressOptions.push({
      value: assetDetails.customerAddressId,
      label: `${assetDetails.address}, ${assetDetails.addressZip} ${assetDetails.addressCity}`,
    })
  }
  const locationUserTypeId = assetTypes?.userTypes.find(type => type.type === 'LOCATION')?.id

  const lockAssetCustomer =
    assetDetails.isLeased &&
    (leasingInfo?.leasingFieldLocks?.assetCustomer ||
      leasingInfo?.lockFields?.includes('customerId'))
  const lockAssetUser =
    assetDetails.isLeased &&
    (leasingInfo?.leasingFieldLocks?.assetUser || leasingInfo?.lockFields?.includes('assetUser'))
  const lockAssetStatus =
    assetDetails.isLeased &&
    (leasingInfo?.leasingFieldLocks?.assetStatus || leasingInfo?.lockFields?.includes('statusId'))

  return (
    <div className="form-column-set">
      <div className="form-column">
        <Field name="customerId">
          {({ field, meta, form }: FieldProps) => (
            <Select
              id="customerId"
              capitalizeLabel
              className="w-fit w-full"
              options={
                customers?.map(customer => ({
                  value: customer.id,
                  label: `${customer.name} - ${customer.orgNo}`,
                })) || []
              }
              label={
                <>
                  <Translation id="Company" />*
                </>
              }
              hitsTranslation={translate('hit(s)')}
              noResultsFoundTranslation={translate('No results found')}
              ariaLabel={translate('Company')}
              isDisabled={lockAssetCustomer || isFormDisabled}
              placeholder={translate('Choose company...')}
              marginBottom={16}
              touched={meta.touched}
              error={meta.error}
              {...field}
              onBlur={() => form.setFieldTouched(field.name, true)}
              onChange={option => {
                if (lockAssetCustomer) return
                form.setFieldValue(field.name, option?.value)
                // Clear an employee when changing company
                form.setFieldValue('costObjectId', undefined)
              }}
            />
          )}
        </Field>

        <Field name="costObjectId">
          {({ field, meta, form }: FieldProps) => (
            <Select
              id="costObjectId"
              capitalizeLabel
              isClearable
              className="w-fit w-full"
              options={
                employees
                  ?.filter(
                    costObj => !costObj.isHidden && costObj.customerId === form.values.customerId
                  )
                  .map(constObj => ({
                    value: constObj.id,
                    label: `${constObj.name} - ${constObj.code}`,
                  })) || []
              }
              label={<Translation id="Username" />}
              hitsTranslation={translate('hit(s)')}
              noResultsFoundTranslation={translate('No results found')}
              ariaLabel={translate('Username')}
              isDisabled={lockAssetUser || isFormDisabled}
              placeholder={translate('Choose employee...')}
              marginBottom={16}
              touched={meta.touched}
              error={meta.error}
              {...field}
              onBlur={() => form.setFieldTouched(field.name, true)}
              onChange={option => {
                if (lockAssetUser) return
                form.setFieldValue(field.name, option?.value)
                if (!option) return
                // We display username and phone number but send only the username
                const selectedEmployee =
                  employees && getCostObjectById(employees, Number(option.value))
                const isLocationUser = selectedEmployee?.isLocation
                if (!isLocationUser) return
                if (typeof locationUserTypeId !== 'number')
                  throw Error('User type id is not a number!')
                form.setFieldValue('userTypeId', locationUserTypeId)
                onChangeUserTypeId(locationUserTypeId)
                form.setFieldValue('customerAddressId', selectedEmployee.customerAddressId)
              }}
            />
          )}
        </Field>

        <Field name="customerAddressId">
          {({ field, form }: FieldProps) => (
            <Select
              capitalizeLabel
              id="customerAddressId"
              label={<Translation id="Location" />}
              options={uniq(addressOptions)}
              isDisabled={isFormDisabled}
              marginBottom={16}
              {...field}
              onChange={option => form.setFieldValue(field.name, option?.value)}
            />
          )}
        </Field>
      </div>

      <div className="form-column">
        <Field name="statusId">
          {({ field, form }: FieldProps) => (
            <Select
              capitalizeLabel
              id="statusId"
              label={
                <>
                  <Translation id="Status" />*
                </>
              }
              options={assetStatusOptions}
              isDisabled={lockAssetStatus || isFormDisabled}
              marginBottom={16}
              {...field}
              onChange={option => form.setFieldValue(field.name, option?.value)}
            />
          )}
        </Field>

        <Field name="userTypeId">
          {({ field, form }: FieldProps) => (
            <Select
              capitalizeLabel
              id="userTypeId"
              label={
                <>
                  <Translation id="User type" />*
                </>
              }
              options={
                assetTypes?.userTypes.map(({ id, type }) => ({
                  value: id,
                  label: translate(`asset_user_type/${type}`),
                })) || []
              }
              isDisabled={isFormDisabled}
              marginBottom={16}
              {...field}
              onChange={option => {
                form.setFieldValue(field.name, option?.value)
                if (typeof option?.value !== 'number') throw Error('User type id is not a number!')
                onChangeUserTypeId(option?.value)
              }}
            />
          )}
        </Field>

        <Field name="comment">
          {({ field, meta }: FieldProps) => (
            <Textarea
              capitalizeLabel
              isFullWidth
              id="comment"
              isDisabled={isFormDisabled}
              marginBottom={16}
              touched={meta.touched}
              error={meta.error}
              label={<Translation id="Note" />}
              {...field}
            />
          )}
        </Field>

        {assetDetails.numberOfInjuryReports > 0 && hasEditAccess && (
          <div>
            <Translation
              id="See injury reports ({numberOfReports})"
              numberOfReports={String(assetDetails.numberOfInjuryReports)}
            />

            <RouterLink
              to={`${APP_ROUTES.HARDWARE_INJURY_REPORTS}?assetId=${assetDetails.id}&status=-1`}
            >
              <Icon className="ml-1" size="small">
                <OpenInNewIcon />
              </Icon>
            </RouterLink>
          </div>
        )}

        <NotificationLogPopup assetId={assetDetails.id} />
      </div>
    </div>
  )
}
